import { withSelectedBundle } from '../utils/selectedAcademicYear';
import { mapYearLockedError } from '../utils/classAcademicYear';
import { supabase } from '../supabaseClient';
import type {
  DailyRecordNoteRow,
  DailyRecordNotesWeekPayload,
  DailyWeekNoteDraft,
} from '../types/dailyRecordNote';
import { fetchDailyNoteCategories } from './dailyNoteCategoriesService';
import { isGeneralDailyCategory } from '../utils/dailyNoteCategoryEdit';
import {
  buildDailyNotesListVersion,
  clearDailyNotesListCache,
  dailyNotesPageRange,
  loadDailyNotesListHeadCache,
  loadDailyNotesListPageCache,
  saveDailyNotesListHeadCache,
  saveDailyNotesListPageCache,
  saveDailyRecordNotesWeekRowsCache,
} from '../utils/dailyRecordNotesCache';
import { mondayIsoForDateInLocal } from '../utils/dashboardDateContext';
import { weekDateIsosFromMonday } from '../utils/dashboardSemesterWeek';
import { parseISODate } from '../utils/date';

/** 이번 세션에서 이미 head 검증된 버전 — 페이지 이동 시 재조회 생략 */
const sessionListHeads = new Map<string, { version: string; totalCount: number }>();

function sessionHeadMemKey(userId: string, categoryId?: string | null): string {
  return `${userId}:${categorySeg(categoryId)}`;
}

/** 이번 세션에 이미 불러온 페이지 본문 — 재방문 시 네트워크 0 */
const sessionPages = new Map<
  string,
  { userId: string; page: number; notes: DailyRecordNoteRow[]; totalCount: number; version: string }
>();

function categorySeg(categoryId?: string | null): string {
  return (categoryId ?? '').trim() || 'all';
}

function sessionPageKey(userId: string, page: number, categoryId?: string | null): string {
  return `${userId}:${categorySeg(categoryId)}:${page}`;
}

function rememberSessionPage(
  userId: string,
  page: number,
  notes: DailyRecordNoteRow[],
  totalCount: number,
  version: string,
  categoryId?: string | null
): void {
  sessionPages.set(sessionPageKey(userId, page, categoryId), {
    userId,
    page,
    notes,
    totalCount,
    version,
  });
}

function loadSessionPage(
  userId: string,
  page: number,
  categoryId?: string | null
): {
  userId: string;
  page: number;
  notes: DailyRecordNoteRow[];
  totalCount: number;
  version: string;
} | null {
  return sessionPages.get(sessionPageKey(userId, page, categoryId)) ?? null;
}

function clearSessionPages(userId?: string): void {
  if (!userId) {
    sessionPages.clear();
    return;
  }
  const prefix = `${userId}:`;
  Array.from(sessionPages.keys()).forEach((key) => {
    if (key.startsWith(prefix)) sessionPages.delete(key);
  });
}

function sessionHeadStorageKey(userId: string, categoryId?: string | null): string {
  return `cp.dailyNotes.sessionHead.v2.${userId}.${categorySeg(categoryId)}`;
}

function clearSessionListHead(userId?: string): void {
  if (!userId) {
    sessionListHeads.clear();
  } else {
    const prefix = `${userId}:`;
    Array.from(sessionListHeads.keys()).forEach((key) => {
      if (key.startsWith(prefix)) sessionListHeads.delete(key);
    });
  }
  clearSessionPages(userId);
  if (typeof sessionStorage === 'undefined') return;
  try {
    const keys: string[] = [];
    const headPrefix = userId
      ? `cp.dailyNotes.sessionHead.v2.${userId}.`
      : 'cp.dailyNotes.sessionHead.v2.';
    const legacyPrefix = userId
      ? `cp.dailyNotes.sessionHead.v1.${userId}`
      : 'cp.dailyNotes.sessionHead.v1.';
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (!k) continue;
      if (k.startsWith(headPrefix) || k.startsWith(legacyPrefix) || k === legacyPrefix) keys.push(k);
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* noop */
  }
}

function rememberSessionListHead(
  userId: string,
  head: { version: string; totalCount: number },
  categoryId?: string | null
): void {
  sessionListHeads.set(sessionHeadMemKey(userId, categoryId), {
    version: head.version,
    totalCount: head.totalCount,
  });
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      sessionHeadStorageKey(userId, categoryId),
      JSON.stringify({ version: head.version, totalCount: head.totalCount })
    );
  } catch {
    /* noop */
  }
}

/** LS/sessionStorage에서 세션 head 복원(네트워크 없음). */
function hydrateSessionListHead(userId: string, categoryId?: string | null): void {
  if (!userId) return;
  if (sessionListHeads.has(sessionHeadMemKey(userId, categoryId))) return;

  if (typeof sessionStorage !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(sessionHeadStorageKey(userId, categoryId));
      if (raw) {
        const parsed = JSON.parse(raw) as { version?: string; totalCount?: number };
        if (typeof parsed.version === 'string' && typeof parsed.totalCount === 'number') {
          sessionListHeads.set(sessionHeadMemKey(userId, categoryId), {
            version: parsed.version,
            totalCount: parsed.totalCount,
          });
          return;
        }
      }
    } catch {
      /* noop */
    }
  }

  const lsHead = loadDailyNotesListHeadCache(userId, categoryId);
  if (lsHead) {
    sessionListHeads.set(sessionHeadMemKey(userId, categoryId), {
      version: lsHead.version,
      totalCount: lsHead.totalCount,
    });
  }
}

function readSessionListHead(
  userId: string,
  categoryId?: string | null
): { version: string; totalCount: number } | null {
  return sessionListHeads.get(sessionHeadMemKey(userId, categoryId)) ?? null;
}

/** 로그아웃·유저 전환 시 메모리 head 클리어 */
export function clearDailyRecordNotesRuntimeState(userId?: string): void {
  clearSessionListHead(userId);
}

function toWeekDraft(row: DailyRecordNoteRow): DailyWeekNoteDraft {
  return {
    id: row.id,
    note_date: row.note_date,
    body: row.body,
    category_id: row.category_id,
    persisted: true,
  };
}

function mondayOfNoteDate(noteDate: string): string | null {
  const d = parseISODate(noteDate);
  if (!d) return null;
  return mondayIsoForDateInLocal(d);
}

async function refreshWeekCacheForDate(userId: string, noteDate: string): Promise<void> {
  const monday = mondayOfNoteDate(noteDate);
  if (!monday) return;
  const dates = weekDateIsosFromMonday(monday);
  const { data, error } = await supabase
    .from('daily_record_notes')
    .select('id, user_id, note_date, body, category_id, created_at, updated_at')
    .match(withSelectedBundle({ user_id: userId }))
    .gte('note_date', dates.find(Boolean) ?? monday)
    .lte('note_date', [...dates].reverse().find(Boolean) ?? monday)
    .order('created_at', { ascending: true });
  if (error) return;
  const notes = ((data ?? []) as Record<string, unknown>[]).map(mapNoteRow).map(toWeekDraft);
  saveDailyRecordNotesWeekRowsCache(userId, monday, notes);
}

function invalidateListCaches(userId: string): void {
  clearSessionListHead(userId);
  clearDailyNotesListCache(userId);
}

/** 주간(월~일) 일자별 기록 로드. */
export async function fetchDailyRecordNotesForWeek(
  userId: string,
  mondayIso: string,
  dateIsos: string[]
): Promise<DailyRecordNotesWeekPayload> {
  if (!userId || !mondayIso) return { notes: [] };

  const from = dateIsos.find(Boolean) ?? mondayIso;
  const to = [...dateIsos].reverse().find(Boolean) ?? mondayIso;

  const { data, error } = await supabase
    .from('daily_record_notes')
    .select('id, user_id, note_date, body, category_id, created_at, updated_at')
    .match(withSelectedBundle({ user_id: userId }))
    .gte('note_date', from)
    .lte('note_date', to)
    .order('created_at', { ascending: true });

  if (error) throw error;

  const notes = ((data ?? []) as Record<string, unknown>[]).map(mapNoteRow).map(toWeekDraft);
  saveDailyRecordNotesWeekRowsCache(userId, mondayIso, notes);
  return { notes };
}

/** 위클리에서 고친 일반 기록·카테고리 쪽지를 한 번에 저장. */
export async function saveDailyRecordNotesWeek(
  userId: string,
  mondayIso: string,
  notes: DailyWeekNoteDraft[],
  baseline: DailyWeekNoteDraft[]
): Promise<DailyWeekNoteDraft[]> {
  if (!userId) throw new Error('로그인이 필요합니다.');
  const now = new Date().toISOString();
  const categories = await fetchDailyNoteCategories(userId);
  const defaultCat = categories.find((c) => isGeneralDailyCategory(c)) ?? categories[0];
  const local = notes.filter((n) => n.persisted || n.body.trim().length > 0);
  const localIds = new Set(local.map((n) => n.id));
  const saved: DailyWeekNoteDraft[] = [];

  for (const n of local) {
    const categoryId = n.category_id ?? defaultCat?.id ?? null;
    const body = n.body;
    if (!n.persisted) {
      const id = crypto.randomUUID();
      const { data, error } = await supabase
        .from('daily_record_notes')
        .insert({
          id,
          user_id: userId,
          note_date: n.note_date,
          body,
          category_id: categoryId,
          updated_at: now,
        })
        .select('id, user_id, note_date, body, category_id, created_at, updated_at')
        .single();
      if (error) throw error;
      saved.push(toWeekDraft(mapNoteRow(data as Record<string, unknown>)));
      continue;
    }
    const base = baseline.find((b) => b.id === n.id);
    if (
      base &&
      base.body === body &&
      (base.category_id ?? null) === categoryId &&
      base.note_date === n.note_date
    ) {
      saved.push({ ...n, category_id: categoryId, persisted: true });
      continue;
    }
    const { data, error } = await supabase
      .from('daily_record_notes')
      .update({
        body,
        category_id: categoryId,
        note_date: n.note_date,
        updated_at: now,
      })
      .eq('id', n.id)
      .eq('user_id', userId)
      .select('id, user_id, note_date, body, category_id, created_at, updated_at')
      .single();
    if (error) throw error;
    saved.push(toWeekDraft(mapNoteRow(data as Record<string, unknown>)));
  }

  for (const b of baseline) {
    if (!b.persisted || localIds.has(b.id)) continue;
    const { error } = await supabase
      .from('daily_record_notes')
      .delete()
      .eq('id', b.id)
      .eq('user_id', userId);
    if (error) throw error;
  }

  invalidateListCaches(userId);
  saveDailyRecordNotesWeekRowsCache(userId, mondayIso, saved);
  return saved;
}

function mapNoteRow(row: Record<string, unknown>): DailyRecordNoteRow {
  return {
    id: String(row.id ?? ''),
    user_id: String(row.user_id ?? ''),
    note_date: String(row.note_date ?? ''),
    body: typeof row.body === 'string' ? row.body : '',
    category_id: typeof row.category_id === 'string' && row.category_id ? row.category_id : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

export type DailyNotesPageResult = {
  notes: DailyRecordNoteRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  version: string;
  fromCache: boolean;
};

/** 가벼운 head: 건수 + 최신 updated_at → 버전 문자열 (1회 요청). */
async function fetchDailyNotesListHead(
  userId: string,
  categoryId?: string | null
): Promise<{
  version: string;
  totalCount: number;
}> {
  let query = supabase
    .from('daily_record_notes')
    .select('updated_at', { count: 'exact' })
    .match(withSelectedBundle({ user_id: userId }))
    .neq('body', '');
  const cat = (categoryId ?? '').trim();
  if (cat) query = query.eq('category_id', cat);
  const { data, error, count } = await query.order('updated_at', { ascending: false }).limit(1);
  if (error) throw error;

  const totalCount = count ?? 0;
  const maxUpdatedAt =
    totalCount > 0 && data?.[0]
      ? String((data[0] as { updated_at?: string }).updated_at ?? '')
      : '';
  return {
    version: buildDailyNotesListVersion(totalCount, maxUpdatedAt),
    totalCount,
  };
}

function pageResultFromCache(
  cachedPage: NonNullable<ReturnType<typeof loadDailyNotesListPageCache>>,
  totalCount: number,
  pageSize: number,
  version: string
): DailyNotesPageResult {
  return {
    notes: cachedPage.notes,
    totalCount,
    page: cachedPage.page,
    pageSize,
    version,
    fromCache: true,
  };
}

function rememberLoadedPage(
  userId: string,
  result: DailyNotesPageResult,
  categoryId?: string | null
): DailyNotesPageResult {
  if (userId && result.page >= 1) {
    rememberSessionPage(
      userId,
      result.page,
      result.notes,
      result.totalCount,
      result.version,
      categoryId
    );
  }
  return result;
}

async function fetchNotesPageRows(
  userId: string,
  from: number,
  to: number,
  categoryId?: string | null
): Promise<DailyRecordNoteRow[]> {
  let query = supabase
    .from('daily_record_notes')
    .select('id, user_id, note_date, body, category_id, created_at, updated_at')
    .match(withSelectedBundle({ user_id: userId }))
    .neq('body', '');
  const cat = (categoryId ?? '').trim();
  if (cat) query = query.eq('category_id', cat);
  const { data, error } = await query
    .order('note_date', { ascending: false })
    .order('created_at', { ascending: false })
    .range(from, to);
  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[])
    .map(mapNoteRow)
    .filter((r) => r.note_date && r.body.trim().length > 0);
}

/**
 * 데일리 노트 목록 페이지 로드.
 * - cacheOnly(페이지 이동): 세션/LS에 있는 페이지는 버전 무관 재사용(네트워크 0).
 *   없는 페이지만 본문 fetch. head 재검증 없음.
 */
export async function fetchDailyRecordNotesPage(
  userId: string,
  page: number,
  options?: { forceRefresh?: boolean; cacheOnly?: boolean; categoryId?: string | null }
): Promise<DailyNotesPageResult> {
  const safePage = Math.max(1, Math.floor(page) || 1);
  const { from, to, pageSize } = dailyNotesPageRange(safePage);
  const categoryId = options?.categoryId ?? null;
  if (!userId) {
    return { notes: [], totalCount: 0, page: safePage, pageSize, version: '', fromCache: false };
  }

  hydrateSessionListHead(userId, categoryId);
  const cachedPage = loadDailyNotesListPageCache(userId, safePage, categoryId);
  const cachedHead = loadDailyNotesListHeadCache(userId, categoryId);
  const memPage = loadSessionPage(userId, safePage, categoryId);
  const sessionHead = readSessionListHead(userId, categoryId);

  if (!options?.forceRefresh) {
    if (options?.cacheOnly) {
      if (memPage) {
        return rememberLoadedPage(
          userId,
          {
            notes: memPage.notes,
            totalCount: memPage.totalCount,
            page: safePage,
            pageSize,
            version: memPage.version,
            fromCache: true,
          },
          categoryId
        );
      }
      if (cachedPage) {
        const totalCount = sessionHead?.totalCount ?? cachedHead?.totalCount ?? cachedPage.totalCount;
        const version = sessionHead?.version ?? cachedHead?.version ?? cachedPage.version;
        if (cachedHead) rememberSessionListHead(userId, cachedHead, categoryId);
        else if (!sessionHead) {
          rememberSessionListHead(userId, { version, totalCount }, categoryId);
        }
        return rememberLoadedPage(
          userId,
          pageResultFromCache(cachedPage, totalCount, pageSize, version),
          categoryId
        );
      }
      const head = sessionHead ?? cachedHead;
      if (head) {
        rememberSessionListHead(userId, head, categoryId);
        const notes = await fetchNotesPageRows(userId, from, to, categoryId);
        saveDailyNotesListPageCache(
          userId,
          {
            version: head.version,
            page: safePage,
            totalCount: head.totalCount,
            notes,
          },
          categoryId
        );
        return rememberLoadedPage(
          userId,
          {
            notes,
            totalCount: head.totalCount,
            page: safePage,
            pageSize,
            version: head.version,
            fromCache: false,
          },
          categoryId
        );
      }
    }

    if (memPage) {
      return rememberLoadedPage(
        userId,
        {
          notes: memPage.notes,
          totalCount: memPage.totalCount,
          page: safePage,
          pageSize,
          version: memPage.version,
          fromCache: true,
        },
        categoryId
      );
    }

    if (sessionHead && cachedPage && cachedPage.version === sessionHead.version) {
      return rememberLoadedPage(
        userId,
        pageResultFromCache(cachedPage, sessionHead.totalCount, pageSize, sessionHead.version),
        categoryId
      );
    }

    if (sessionHead) {
      const notes = await fetchNotesPageRows(userId, from, to, categoryId);
      saveDailyNotesListPageCache(
        userId,
        {
          version: sessionHead.version,
          page: safePage,
          totalCount: sessionHead.totalCount,
          notes,
        },
        categoryId
      );
      return rememberLoadedPage(
        userId,
        {
          notes,
          totalCount: sessionHead.totalCount,
          page: safePage,
          pageSize,
          version: sessionHead.version,
          fromCache: false,
        },
        categoryId
      );
    }

    const head = await fetchDailyNotesListHead(userId, categoryId);
    rememberSessionListHead(userId, head, categoryId);
    saveDailyNotesListHeadCache(userId, head, categoryId);

    if (cachedPage && cachedPage.version === head.version) {
      return rememberLoadedPage(
        userId,
        pageResultFromCache(cachedPage, head.totalCount, pageSize, head.version),
        categoryId
      );
    }

    const notes = await fetchNotesPageRows(userId, from, to, categoryId);
    saveDailyNotesListPageCache(
      userId,
      {
        version: head.version,
        page: safePage,
        totalCount: head.totalCount,
        notes,
      },
      categoryId
    );
    return rememberLoadedPage(
      userId,
      {
        notes,
        totalCount: head.totalCount,
        page: safePage,
        pageSize,
        version: head.version,
        fromCache: false,
      },
      categoryId
    );
  }

  clearSessionListHead(userId);
  const notes = await fetchNotesPageRows(userId, from, to, categoryId);
  const head = await fetchDailyNotesListHead(userId, categoryId);
  rememberSessionListHead(userId, head, categoryId);
  clearDailyNotesListCache(userId);
  saveDailyNotesListHeadCache(userId, { version: head.version, totalCount: head.totalCount }, categoryId);
  saveDailyNotesListPageCache(
    userId,
    {
      version: head.version,
      page: safePage,
      totalCount: head.totalCount,
      notes,
    },
    categoryId
  );
  return rememberLoadedPage(
    userId,
    {
      notes,
      totalCount: head.totalCount,
      page: safePage,
      pageSize,
      version: head.version,
      fromCache: false,
    },
    categoryId
  );
}

/** 캐시가 있으면 즉시 반환용(동기). 버전 검증 전 UI 선표시. */
export function peekDailyRecordNotesPageCache(
  userId: string,
  page: number,
  categoryId?: string | null
): DailyNotesPageResult | null {
  if (!userId) return null;
  hydrateSessionListHead(userId, categoryId);
  const safePage = Math.max(1, Math.floor(page) || 1);
  const { pageSize } = dailyNotesPageRange(safePage);

  const memPage = loadSessionPage(userId, safePage, categoryId);
  if (memPage) {
    return {
      notes: memPage.notes,
      totalCount: memPage.totalCount,
      page: safePage,
      pageSize,
      version: memPage.version,
      fromCache: true,
    };
  }

  const cachedPage = loadDailyNotesListPageCache(userId, safePage, categoryId);
  if (!cachedPage) return null;

  const sessionHead = readSessionListHead(userId, categoryId);
  if (sessionHead) {
    return pageResultFromCache(
      cachedPage,
      sessionHead.totalCount,
      pageSize,
      sessionHead.version || cachedPage.version
    );
  }

  const cachedHead = loadDailyNotesListHeadCache(userId, categoryId);
  if (cachedHead) {
    rememberSessionListHead(userId, cachedHead, categoryId);
    return pageResultFromCache(cachedPage, cachedHead.totalCount, pageSize, cachedHead.version);
  }

  return pageResultFromCache(cachedPage, cachedPage.totalCount, pageSize, cachedPage.version);
}

export async function upsertDailyRecordNote(
  userId: string,
  noteDate: string,
  body: string,
  options?: { id?: string | null; categoryId?: string | null }
): Promise<DailyRecordNoteRow> {
  if (!userId) throw new Error('로그인이 필요합니다.');
  if (!noteDate) throw new Error('날짜를 선택해 주세요.');
  const now = new Date().toISOString();
  const id = options?.id?.trim() || crypto.randomUUID();
  const { data, error } = await supabase
    .from('daily_record_notes')
    .upsert(
      {
        id,
        user_id: userId,
        note_date: noteDate,
        body,
        category_id: options?.categoryId ?? null,
        updated_at: now,
      },
      { onConflict: 'id' }
    )
    .select('id, user_id, note_date, body, category_id, created_at, updated_at')
    .single();
  if (error) throw mapYearLockedError(error);
  await refreshWeekCacheForDate(userId, noteDate);
  invalidateListCaches(userId);
  return mapNoteRow(data as Record<string, unknown>);
}

export async function deleteDailyRecordNote(userId: string, noteId: string): Promise<void> {
  if (!userId || !noteId) return;
  const { data: existing } = await supabase
    .from('daily_record_notes')
    .select('note_date')
    .match(withSelectedBundle({ user_id: userId }))
    .eq('id', noteId)
    .maybeSingle();
  const { error } = await supabase
    .from('daily_record_notes')
    .delete()
    .eq('user_id', userId)
    .eq('id', noteId);
  if (error) throw error;
  const noteDate = existing ? String((existing as { note_date?: string }).note_date ?? '') : '';
  if (noteDate) await refreshWeekCacheForDate(userId, noteDate);
  invalidateListCaches(userId);
}

export { DAILY_NOTES_PAGE_SIZE, dailyNotesTotalPages } from '../utils/dailyRecordNotesCache';
